/** Native Skio payload preparation. Shopify/Skio remain the price and inventory authorities.
 * These checks protect the selector flow; they are not server-side checkout enforcement.
 */
(function (root) {
  'use strict';
  const gid = (kind, id) => `gid://shopify/${kind}/${String(id).split('/').pop()}`;
  const shortId = (id) => String(id).split('/').pop();
  const cents = (amount) => Math.round(Number(amount) * 100);
  function fail(message) {
    const error = new Error(message);
    error.nativeBoxMessage = true;
    throw error;
  }
  function prepare({ items, cart, box, tiers, subscriptionDiscount }) {
    const unavailable = 'Subscription pricing is temporarily unavailable. Please try again shortly.';
    if (!box?.boxId || !Array.isArray(items) || !items.length || cart?.currency !== box.currencyCode) fail(unavailable);
    const planIds = new Set(items.map((item) => String(item.selling_plan || '')));
    if (planIds.size !== 1 || planIds.has('')) fail(unavailable);
    const selectedPlan = [...planIds][0];
    const plan = box.sellingPlanGroup?.[gid('SellingPlan', selectedPlan)];
    if (!plan || plan.priceAdjustmentType !== 'PERCENTAGE' || Number(plan.priceAdjustmentAmount) !== subscriptionDiscount) fail(unavailable);
    const variants = new Set((box.selectableProductVariants || []).flatMap((product) => product.productVariants.map((v) => shortId(v.platformId))));
    const checked = (id, quantity) => {
      if (!variants.has(shortId(id)) || !Number.isSafeInteger(quantity) || quantity < 1) fail(unavailable);
      const mapping = plan.variantMapping?.[gid('ProductVariant', id)];
      if (!mapping || shortId(mapping.planId) !== selectedPlan || !Number.isFinite(Number(mapping.adjustedPrice))) fail(unavailable);
      return cents(mapping.adjustedPrice) * quantity;
    };
    let addedQuantity = 0;
    let addedBase = 0;
    for (const item of items) {
      addedBase += checked(item.id, item.quantity);
      addedQuantity += item.quantity;
    }
    const groups = new Map();
    for (const line of cart.items || []) {
      const linePlan = String(line.selling_plan_allocation?.selling_plan?.id || '');
      if (line.properties?._dynamicBoxId !== box.boxId) {
        if (linePlan === selectedPlan && variants.has(String(line.variant_id))) {
          fail('Please remove your existing soap subscription from the cart, then add your complete scent selection.');
        }
        continue;
      }
      const index = String(line.properties._dynamicBoxIndex ?? '');
      if (!/^\d+$/.test(index) || !Number.isSafeInteger(Number(index))) fail(unavailable);
      if (!groups.has(index)) groups.set(index, []);
      groups.get(index).push(line);
    }
    const matching = [...groups].filter(([, lines]) => lines.some((line) => String(line.selling_plan_allocation?.selling_plan?.id || '') === selectedPlan));
    if (matching.length > 1) fail('Please update your existing soap subscriptions in the cart before adding more bars.');
    let index = String(groups.size ? Math.max(...[...groups.keys()].map(Number)) + 1 : 0);
    let combinedQuantity = addedQuantity;
    let combinedBase = addedBase;
    if (matching.length) {
      index = matching[0][0];
      for (const line of matching[0][1]) {
        if (String(line.selling_plan_allocation?.selling_plan?.id || '') !== selectedPlan) fail(unavailable);
        combinedBase += checked(line.variant_id, line.quantity);
        combinedQuantity += line.quantity;
      }
    }
    const [minimum, maximum] = box.sizeRange || [];
    if (!Number.isSafeInteger(combinedQuantity) || combinedQuantity < Number(minimum || 1)) fail(unavailable);
    if (maximum != null && combinedQuantity > Number(maximum)) {
      fail(`This soap subscription supports up to ${maximum} bars. Please reduce the quantity before adding it.`);
    }
    if (box.sizeInterval?.length && !box.sizeInterval.map(Number).includes(combinedQuantity)) fail(unavailable);
    const tier = tiers.find((entry) => entry.max === null || combinedQuantity <= entry.max);
    const discountThresholds = Object.keys(box.fixedDiscountTier || {}).map(Number).filter((value) => value <= combinedQuantity).sort((a, b) => b - a);
    if (!tier || box.percentDiscountTier || !discountThresholds.length) fail(unavailable);
    const nativeTotal = combinedBase - cents(box.fixedDiscountTier[String(discountThresholds[0])]);
    const expectedTotal = Math.round(combinedQuantity * tier.price * (100 - subscriptionDiscount) / 100);
    if (nativeTotal !== expectedTotal) fail(unavailable);
    return {
      items: items.map((item) => ({ ...item, properties: { ...(item.properties || {}), _dynamicBoxId: box.boxId, _dynamicBoxIndex: index } })),
      boxIndex: index, combinedQuantity, expectedTotal
    };
  }
  root.BmsNativeBox = { prepare };
  if (typeof module !== 'undefined' && module.exports) module.exports = { prepare };
})(typeof window !== 'undefined' ? window : globalThis);
